import { useMemo, useState, useCallback } from 'react';
import { useParams } from 'react-router';

import {
  Box,
  List,
  ListItem,
  ListItemText,
  Stack,
  Typography
} from '@mui/material';
import { GridRowSelectionModel } from '@mui/x-data-grid';

import { useQueryClient } from '@tanstack/react-query';

import AutocompleteCreators from '@/components/autocompleteCreators';
import Button from '@/components/button';
import Dialog from '@/components/dialog/Dialog';
import { useAddCreatorsToGroups } from '@/hooks/queries/creatorGroups';
import { useFetchCreatorGroupsList } from '@/hooks/queries/creators';
import useAlert from '@/hooks/useAlert';
import { Creator, CreatorGroups } from '@/models/creator';

interface Props {
  isOpen: boolean;
  onClose: () => void;
  clearSelection: () => void;
  selected: GridRowSelectionModel;
  creatorsList: Array<Creator.Item>;
}

const AddCreatorsGroups = ({
  isOpen,
  onClose,
  clearSelection,
  selected,
  creatorsList
}: Props) => {
  const id = Number(useParams().workspaceId);
  const queryClient = useQueryClient();
  const alert = useAlert();

  const [groups, setGroups] = useState<CreatorGroups.Group[]>([]);

  const { data: creatorsGroups = [] } = useFetchCreatorGroupsList({ id });

  const { mutate: addCreatorsToGroups, isPending: isAdding } =
    useAddCreatorsToGroups({
      onSuccess: () => {},
      onError: () => {
        alert.error(
          'Some error occurred while adding creators to the group(s)'
        );
      }
    });

  const handleSelectGroup = (
    _: React.ChangeEvent<HTMLInputElement>,
    value: CreatorGroups.Group[]
  ) => {
    setGroups(value);
  };

  const groupsOptions = useMemo(
    () =>
      creatorsGroups.map((group) => ({
        ...group,
        name: group.title,
        type: 'Groups' as const
      })),
    [creatorsGroups]
  );

  const invalidateQueries = useCallback(async () => {
    await Promise.all([
      queryClient.invalidateQueries({
        queryKey: ['creatorGroupsList', id],
        refetchType: 'all'
      }),
      queryClient.invalidateQueries({
        queryKey: ['creatorGroup'],
        refetchType: 'all'
      }),
      queryClient.invalidateQueries({
        queryKey: ['creator'],
        refetchType: 'all'
      })
    ]);
  }, [queryClient, id]);

  const handleSubmit = useCallback(() => {
    const groupIds = groups.map((group) => group.id);
    const creatorIds = selected as number[];

    addCreatorsToGroups(
      { groupIds, creatorIds },
      {
        onSuccess: async () => {
          await invalidateQueries();

          const groupText = groupIds.length > 1 ? 'groups' : 'group';
          const creatorText = creatorIds.length > 1 ? 'Creators' : 'Creator';
          alert.success(
            `${creatorText} successfully added to the ${groupText}!`
          );

          onClose();
          clearSelection();
        }
      }
    );
  }, [
    groups,
    selected,
    addCreatorsToGroups,
    invalidateQueries,
    alert,
    onClose,
    clearSelection
  ]);

  const selectedCreators = creatorsList.filter((creator) =>
    selected.includes(creator.id)
  );

  const count = selected.length;
  const label = count === 1 ? 'creator' : 'creators';

  return (
    <Dialog
      title="Add to group"
      fullWidth
      open={isOpen}
      onClose={onClose}
      dialogContent={
        <Stack gap={1}>
          <Box>
            <Typography>{`Adding ${count} ${label} to:`}</Typography>
          </Box>
          <AutocompleteCreators
            value={groups}
            onChange={handleSelectGroup}
            creatorGroups={groupsOptions}
            label="Creator Groups"
          />
          <Box border="1px solid gray">
            <List
              sx={{ maxHeight: 360, overflowY: 'auto' }}
              dense
              disablePadding
            >
              {selectedCreators.map((creator) => (
                <ListItem
                  key={creator.id}
                  disableGutters
                  sx={{
                    px: 2,
                    py: 0.5,
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                    '&::before': {
                      content: '"•"',
                      color: 'text.secondary',
                      fontWeight: 'bold',
                      fontSize: '1.1rem',
                      lineHeight: 1
                    }
                  }}
                >
                  <ListItemText
                    primary={creator.name}
                    primaryTypographyProps={{ variant: 'body2' }}
                  />
                </ListItem>
              ))}
            </List>
          </Box>
        </Stack>
      }
      actions={
        <>
          <Button onClick={onClose} variant="text" text="Cancel" />
          <Button
            text="Add to group"
            onClick={handleSubmit}
            loading={isAdding}
            disabled={!groups.length}
          />
        </>
      }
    />
  );
};

export default AddCreatorsGroups;
